import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import UsersTable from './UsersTable';

const ROW = {
  id: 1,
  name: 'Abe Martinez',
  email: 'martinez@email.com',
  jobRole: 'Fleet Manager',
  accounts: ['Account Name A'],
  permissions: ['Fleet Administrator'],
  permissionAssignments: [
    { account: 'Account Name A', permission: 'Fleet Administrator' },
  ],
  lastLoginAt: '03/08/2025 05:22 PM MST',
};

const PAGINATION = { page: 1, perPage: 25, totalItems: 1, totalPages: 1 };

const renderTable = (props = {}) =>
  render(
    <ConfigProvider>
      <UsersTable
        pagination={PAGINATION}
        users={[ROW]}
        onPageChange={jest.fn()}
        onSort={() => jest.fn()}
        {...props}
      />
    </ConfigProvider>,
  );

const headers = () =>
  screen.queryAllByRole('columnheader').map((cell) => cell.textContent);

describe('management/Users UsersTable', () => {
  describe('the columns', () => {
    it('renders the five the design calls for, in order', () => {
      renderTable();

      expect(headers().join('|')).toMatch(
        /Users.*\|Accounts\|Permissions\|Job Role.*\|Last Login/,
      );
    });

    // Teams wait on PD-4213.
    it('has no Teams column', () => {
      renderTable();

      expect(headers().join(' ')).not.toMatch(/Teams/);
    });

    it('falls back to a placeholder for a missing job role', () => {
      renderTable({ users: [{ ...ROW, jobRole: null }] });

      expect(screen.getAllByText('—').length).toBeGreaterThan(0);
    });

    it('falls back to a placeholder for a user who never logged in', () => {
      renderTable({ users: [{ ...ROW, lastLoginAt: null }] });

      expect(screen.getAllByText('—').length).toBeGreaterThan(0);
    });
  });

  describe('sorting', () => {
    it('offers sorting on the columns the query supports', () => {
      renderTable();

      ['Users', 'Job Role', 'Last Login'].forEach((name) => {
        expect(
          screen.getByRole('button', { name: new RegExp(`${name} `, 'i') }),
        ).toBeInTheDocument();
      });
    });

    // The query cannot order by these — they are aggregated per user.
    it('does not offer sorting on accounts or permissions', () => {
      renderTable();

      expect(
        screen.queryByRole('button', { name: /accounts unsorted/i }),
      ).not.toBeInTheDocument();
      expect(
        screen.queryByRole('button', { name: /permissions unsorted/i }),
      ).not.toBeInTheDocument();
    });

    it('calls the handler for the clicked column', () => {
      const handler = jest.fn();
      renderTable({ onSort: () => handler });

      fireEvent.click(screen.getByRole('button', { name: /job role /i }));

      expect(handler).toHaveBeenCalled();
    });
  });

  describe('states', () => {
    it('shows a spinner while loading', () => {
      renderTable({ isLoading: true, users: [] });

      expect(screen.getByText('Loading...')).toBeInTheDocument();
    });

    it('reports a failure rather than an empty table', () => {
      renderTable({ error: 'boom', users: [] });

      expect(screen.getByText(/Error loading users: boom/)).toBeInTheDocument();
    });

    it('prefers the error over the rows it still holds', () => {
      renderTable({ error: 'boom' });

      expect(screen.queryByText('Abe Martinez')).not.toBeInTheDocument();
    });

    // Figma 787:78031 — nothing at all drops the headers and the footer.
    describe('with no users and nothing narrowing them', () => {
      it('shows the empty state', () => {
        renderTable({ users: [] });

        expect(screen.getByText('No Users Yet')).toBeInTheDocument();
      });

      it('drops the column headers', () => {
        renderTable({ users: [] });

        expect(headers()).toHaveLength(0);
      });

      it('drops the footer', () => {
        renderTable({
          users: [],
          pagination: { ...PAGINATION, totalItems: 0 },
        });

        expect(screen.queryByText(/of 0 Users/)).not.toBeInTheDocument();
      });
    });

    // Figma 787:78141 — a search or filter that matches nothing keeps both, so
    // the user can undo what they narrowed.
    describe('with no users because of a search', () => {
      const props = {
        users: [],
        isNarrowed: true,
        query: 'nobody',
        pagination: { ...PAGINATION, totalItems: 0, totalPages: 0 },
      };

      it('shows the no-matches state instead', () => {
        renderTable(props);

        expect(screen.getByText(/No Matches for/)).toBeInTheDocument();
        expect(screen.queryByText('No Users Yet')).not.toBeInTheDocument();
      });

      it('quotes the term back', () => {
        renderTable(props);

        expect(screen.getByText('No Matches for “nobody”')).toBeInTheDocument();
      });

      it('keeps the column headers', () => {
        renderTable(props);

        expect(headers().length).toBeGreaterThan(0);
      });

      it('keeps the footer', () => {
        renderTable(props);

        expect(screen.getByText(/of 0 Users/)).toBeInTheDocument();
      });
    });

    // Filters can empty the list without a search term, which the design does
    // not draw — no empty pair of quotes.
    it('drops the quotes when only filters narrowed the list', () => {
      renderTable({
        users: [],
        isNarrowed: true,
        pagination: { ...PAGINATION, totalItems: 0, totalPages: 0 },
      });

      expect(screen.getByText('No Matches')).toBeInTheDocument();
    });
  });

  describe('pagination', () => {
    it('reports the range and total', () => {
      renderTable({
        pagination: { page: 1, perPage: 25, totalItems: 95, totalPages: 4 },
      });

      expect(screen.getByText(/1 - 25 of 95 Users/)).toBeInTheDocument();
    });

    it('asks for the chosen page', () => {
      const onPageChange = jest.fn();
      renderTable({
        onPageChange,
        pagination: { page: 1, perPage: 25, totalItems: 95, totalPages: 4 },
      });

      fireEvent.click(screen.getByRole('button', { name: 'Go to page 2' }));

      // KeyDS Pagination reports (page, previousPage); only the first matters.
      expect(onPageChange.mock.calls[0][0]).toEqual(2);
    });
  });
});
